import React from 'react';
import ActionItem from './ActionItem';
import Heading from '@ramsey-design-system/heading';
import getCurrentActionItem from '../../lib/getCurrentActionItem';
import { ActionGroup, ActionItem as ActionItemType } from '../../types';

// Styles
import '@ramsey-design-system/heading/dist/heading.css';
import styles from './action-items.module.css';
import Icon from '@ramsey-design-system/icon';
import { SuccessIcon } from '@ramsey-design-system/icons';
import { PlayIcon } from '@ramsey-design-system/icons';
import { TimeOutlinedIcon } from '@ramsey-design-system/icons';
import Button from '@ramsey-design-system/button';

type CombinedActionGroup = {
  action_items: ActionItemType[];
  title?: string;
};

type ActionItemsProps = {
  actionGroup: ActionGroup;
  lessonNumber?: string | undefined;
  title?: string;
  truncateText?: boolean | undefined;
  urlPrefix?: string;
  showContent?: boolean;
  showLessonTag?: boolean;
  showTitle?: boolean;
  showCurrentActionItem?: boolean;
  currentActionItemId?: string;
  showStepper?: boolean;
  stepperBackgroundColor?: string;
  compact?: boolean;
  collapse?: boolean;
  collapsedActionGroupId?: string;
  className?: string;
  isLastActionGroup?: boolean;
  showLock?: boolean;
  disableActionItem?: boolean;
  showPills?: boolean;
  showVideoItems?: boolean;
  chevronRightIcon?: boolean;
};

const BASE_WHITE = '#ffffff';

export function constructUrlForActionItem(
  item: ActionItemType,
  prefix: string
) {
  return `${prefix}/${item.course_id}/${item.lesson_id}/${item.id}`;
}

function renderActionItems(
  items: ActionItemType[],
  lessonNumber: string | undefined,
  truncateText: boolean | undefined,
  urlPrefix: string,
  showContent: boolean,
  showLessonTag: boolean,
  showCurrentActionItem: boolean,
  currentActionItemId: string | undefined,
  showStepper: boolean,
  stepperBackgroundColor: string,
  showLock: boolean,
  disableActionItem: boolean,
  showVideoItems: boolean,
  chevronRightIcon?: boolean
) {
  const currentActionItem = showCurrentActionItem
    ? getCurrentActionItem(items, currentActionItemId)
    : null;

  return items
    .map((item: ActionItemType, idx: number) => {
      const content = item.body?.content_blocks?.[0].text;
      const isActionItemDisabled =
        disableActionItem && !item.complete
          ? undefined
          : constructUrlForActionItem(item, urlPrefix);

      if (item.template === 'lesson-video' && showVideoItems) {
        const isComplete = item.complete;

        const currentActionItemUrl = `${urlPrefix}/${item.course_id}/${item.lesson_id}/${item.id}`;

        const buttonText =
          item.position! > 0 && !isComplete
            ? `Resume`
            : item.position === 0 && !isComplete
            ? `Start`
            : `Watch Again`;

        const buttonStyle = buttonText === 'Watch Again' ? 'ghost' : 'primary';

        const dataLoggerElementId = `${buttonText} Button`;
        const dataLoggerElementLabel = buttonText;

        const stepperLineClassNames = showStepper
          ? `${styles.vertical_line}`
          : '';
        const stepperBackground = showStepper
          ? { backgroundColor: stepperBackgroundColor }
          : {};

        const onVideoItemClick = (url: string) => {
          window.location.href = url;
        };

        const videoDuration = `${Math.trunc(item.duration! / 60)} Min`;

        return (
          <div
            key={idx}
            onClick={() => onVideoItemClick(currentActionItemUrl)}
            className={styles.lesson_video}
            data-testid="link"
            data-logger-element-id={`Action Item: ${item.title}`}
            data-logger-element-label={`${lessonNumber ? lessonNumber : ''}: ${
              item.title
            }`}
          >
            <div style={stepperBackground} className={styles.checkbox}>
              {isComplete ? (
                <Icon
                  icon={SuccessIcon}
                  color="positive"
                  size="large"
                  data-testid="completed-check"
                />
              ) : (
                <div className={styles.circle} data-testid="incomplete-check" />
              )}
              <div className={stepperLineClassNames} />
            </div>
            <div className={styles.lesson_video_container}>
              <div className={styles.thumbnail}>
                <img className={styles.image} src={item.preview_image} />
              </div>
              <div className={styles.lesson_details}>
                <h2 className={styles.video_heading}>{item.video_title}</h2>
                <div className={styles.duration}>
                  <Icon className={styles.clock_icon} icon={TimeOutlinedIcon} />
                  {videoDuration}
                </div>
              </div>
              <div className={styles.button_container}>
                <Button
                  key={buttonText}
                  element={'a'}
                  appearance={buttonStyle}
                  href={currentActionItemUrl}
                  icon={PlayIcon}
                  className={styles.button_style}
                  data-testid="action-button"
                  data-logger-element-id={dataLoggerElementId}
                  data-logger-element-label={dataLoggerElementLabel}
                >
                  {buttonText}
                </Button>
              </div>
            </div>
          </div>
        );
      }

      return (
        <ActionItem
          key={idx}
          lessonNumber={lessonNumber}
          title={item.title}
          href={isActionItemDisabled}
          content={content || ''}
          completed={item.complete}
          truncateText={truncateText}
          showContent={showContent}
          showLessonTag={showLessonTag}
          current={showCurrentActionItem && currentActionItem?.id === item.id}
          showStepper={showStepper}
          stepperBackgroundColor={stepperBackgroundColor}
          showLock={showLock}
          chevronRightIcon={chevronRightIcon}
        />
      );
    })
    .filter(Boolean);
}

function renderCollapsedActionItems(
  actionGroup: ActionGroup | CombinedActionGroup,
  lessonNumber: string | undefined,
  truncateText: boolean | undefined,
  urlPrefix: string,
  showContent: boolean,
  showLessonTag: boolean,
  showCurrentActionItem: boolean,
  currentActionItemId: string | undefined,
  showStepper: boolean,
  stepperBackgroundColor: string,
  disableActionItem: boolean,
  showLock: boolean,
  showPill: boolean,
  chevronRightIcon?: boolean
) {
  const items = actionGroup.action_items;
  if (items.length === 0) {
    return null;
  }

  const currentActionItem = showCurrentActionItem
    ? getCurrentActionItem(items, currentActionItemId)
    : null;

  const matchedIds = items.filter(item => item.id === currentActionItem?.id);
  const isCurrentItem = matchedIds.length > 0;

  const itemToShow =
    isCurrentItem && currentActionItem ? currentActionItem : items[0];

  const totalItems = items.length;
  const completeItems = items
    .map(item => (item.complete ? (1 as number) : 0))
    .reduce((prev, current) => prev + current, 0);

  const completed = completeItems === totalItems;

  const pillContent = showPill ? (
    <span>
      {completeItems} / {totalItems} &nbsp; Complete
    </span>
  ) : (
    undefined
  );

  const isActionItemDisabled = disableActionItem
    ? undefined
    : constructUrlForActionItem(itemToShow, urlPrefix);

  return (
    <ActionItem
      lessonNumber={lessonNumber}
      title={actionGroup.title || ''}
      href={isActionItemDisabled}
      content={''}
      completed={completed}
      truncateText={truncateText}
      showContent={showContent}
      showLessonTag={showLessonTag}
      current={isCurrentItem}
      showStepper={showStepper}
      stepperBackgroundColor={stepperBackgroundColor}
      pillContent={pillContent}
      showLock={showLock}
      chevronRightIcon={chevronRightIcon}
    />
  );
}

/**
 * Renders action items based on the `ActionGroup`. The `collapse` property can be used
 * to render one action item to represent the group. `collapsedActionGroupId` can be used
 * (if `collapse` is false or default) to collapse based on the action group's id
 *
 * @param ActionItemsProps
 * @returns
 */
function ActionItems({
  actionGroup,
  lessonNumber,
  title,
  truncateText,
  urlPrefix = '/learn',
  showContent = true,
  showLessonTag = true,
  showTitle = true,
  showCurrentActionItem = false,
  showStepper = false,
  stepperBackgroundColor = BASE_WHITE,
  compact = false,
  collapse = false,
  currentActionItemId,
  collapsedActionGroupId = undefined,
  className = '',
  isLastActionGroup = false,
  showLock = false,
  disableActionItem = false,
  showPills = false,
  showVideoItems = false,
  chevronRightIcon = false,
}: ActionItemsProps) {
  if (!actionGroup) return null;

  const classNames = compact
    ? `${styles.container} ${styles.compact} ${className}`
    : `${styles.container} ${className}`;

  const itemsContainerClassNames = isLastActionGroup
    ? `${styles.items_container} ${styles.last_items_container}`
    : `${styles.items_container}`;

  const shouldCollapse =
    collapse ||
    (collapsedActionGroupId && collapsedActionGroupId === actionGroup.id);

  return (
    <div className={classNames} data-testid="action-items">
      {showTitle ? (
        <Heading
          level="2"
          size="medium"
          className={styles.title}
          data-testid="action-items-title"
        >
          {title || actionGroup.title}
        </Heading>
      ) : null}
      <div className={itemsContainerClassNames}>
        {shouldCollapse
          ? renderCollapsedActionItems(
              actionGroup,
              lessonNumber,
              truncateText,
              urlPrefix,
              showContent,
              showLessonTag,
              showCurrentActionItem,
              currentActionItemId,
              showStepper,
              stepperBackgroundColor,
              disableActionItem,
              showLock,
              showPills,
              chevronRightIcon
            )
          : renderActionItems(
              actionGroup.action_items,
              lessonNumber,
              truncateText,
              urlPrefix,
              showContent,
              showLessonTag,
              showCurrentActionItem,
              currentActionItemId,
              showStepper,
              stepperBackgroundColor,
              showLock,
              disableActionItem,
              showVideoItems,
              chevronRightIcon
            )}
      </div>
    </div>
  );
}

export default ActionItems;
